import React, { useState, useEffect, useCallback } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Botão flutuante para rolar a página até o topo
 * Aparece somente após o usuário rolar para baixo
 *
 * @param {number} showAfterScroll - Pixels de scroll antes de mostrar o botão (padrão: 400)
 */
export const ScrollToTopButton = ({ showAfterScroll = 400 }) => {
  const [isVisible, setIsVisible] = useState(false);

  /**
   * Handler para rolar suavemente até o topo
   */
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  /**
   * Monitora o scroll da página para mostrar/ocultar o botão
   */
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > showAfterScroll) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Adiciona listener de scroll com throttle passivo
    window.addEventListener("scroll", toggleVisibility, { passive: true });

    // Verifica posição inicial
    toggleVisibility();

    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, [showAfterScroll]);

  // Suporte para teclado
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        scrollToTop();
      }
    },
    [scrollToTop]
  );

  return (
    <button
      onClick={scrollToTop}
      onKeyDown={handleKeyDown}
      aria-label="Voltar ao topo da página"
      className={`fixed fab-slot-0 right-4 sm:right-5 z-50 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white hover:bg-brand-50 text-cocoa hover:text-brand-600 border border-cream-200 rounded-2xl shadow-md transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/40 ${
        isVisible
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-10 scale-90 pointer-events-none"
      }`}
    >
      <ArrowUp className="w-5 h-5" />
      <span className="sr-only">Voltar ao topo</span>
    </button>
  );
};
